// src/components/Payment.jsx - DARK THEME VERSION

import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import './Payment.css';

const stripePromise = loadStripe(config.STRIPE_PUBLISHABLE_KEY);

const Payment = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pricing, setPricing] = useState(null);
  const { currentUser, daysRemaining } = useAuth();

  // Pricing configuration
  const PRICING_CONFIG = {
    IN: {
      currency: '₹',
      monthly: {
        amount: '49',
        priceId: config.STRIPE_PRICE_MONTHLY_INR || config.STRIPE_PRICE_MONTHLY
      },
      yearly: {
        amount: '499',
        priceId: config.STRIPE_PRICE_ANNUAL_INR || config.STRIPE_PRICE_ANNUAL
      }
    },
    default: {
      currency: '$',
      monthly: {
        amount: '1.39',
        priceId: config.STRIPE_PRICE_MONTHLY
      },
      yearly: {
        amount: '13.99',
        priceId: config.STRIPE_PRICE_ANNUAL
      }
    }
  };

  // Detect country
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace');
        const data = await response.text();
        
        const lines = data.trim().split('\n');
        const traceData = {};
        
        lines.forEach(line => {
          const [key, value] = line.split('=');
          if (key && value) {
            traceData[key] = value;
          }
        });
        
        const country = traceData.loc || 'US';
        console.log('Country detected:', country);
        
        if (country === 'IN') {
          setPricing(PRICING_CONFIG.IN);
        } else {
          setPricing(PRICING_CONFIG.default);
        }
      } catch (error) {
        console.error('Error detecting country:', error);
        setPricing(PRICING_CONFIG.default);
      }
    };

    detectCountry();
  }, []);

  const handleCheckout = async (priceId) => {
    try {
      setLoading(true);
      setError('');
      
      const stripe = await stripePromise;
      
      const response = await fetch(`${config.BACKEND_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: priceId,
          userId: currentUser.uid,
          email: currentUser.email
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const session = await response.json();

      const result = await stripe.redirectToCheckout({
        sessionId: session.id,
      });

      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError('Failed to initialize checkout. Please try again.');
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!pricing) {
    return (
      <div className="payment-overlay" onClick={onClose}>
        <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={onClose}>×</button>
          <div className="payment-header">
            <h2>Loading pricing...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <div className="payment-header">
          <h2>Upgrade to Premium</h2>
          <p className="hero-text">Unlock unlimited access to all games</p>
          {daysRemaining > 0 && (
            <p className="trial-info-text">
              {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining in your trial
            </p>
          )}
        </div>

        {error && <div className="payment-error">{error}</div>}

        <div className="pricing-plans">
          {/* Monthly Plan */}
          <div className="pricing-card">
            <h3>Monthly</h3>
            <div className="price">
              <span className="currency">{pricing.currency}</span>
              <span className="amount">{pricing.monthly.amount}</span>
              <span className="period">/month</span>
            </div>
            <ul className="features">
              <li>✓ All games unlocked</li>
              <li>✓ New games added regularly</li>
              <li>✓ Cancel anytime</li>
            </ul>
            <button 
              className="select-button"
              onClick={() => handleCheckout(pricing.monthly.priceId)}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Subscribe Monthly'}
            </button>
          </div>

          {/* Yearly Plan */}
          <div className="pricing-card popular">
            <div className="popular-badge">BEST VALUE</div>
            <h3>Yearly</h3>
            <div className="price">
              <span className="currency">{pricing.currency}</span>
              <span className="amount">{pricing.yearly.amount}</span>
              <span className="period">/year</span>
            </div>
            {/* <p className="savings">Save {pricing.currency === '$' ? '17%' : '17%'}</p> */}
            <ul className="features">
              <li>✓ All games unlocked</li>
              <li>✓ New games added regularly</li>
              <li>✓ Cancel anytime</li>
            </ul>
            <button 
              className="select-button popular-button"
              onClick={() => handleCheckout(pricing.yearly.priceId)}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Subscribe Yearly'}
            </button>
          </div>
        </div>

        <div className="payment-footer">
          <p>💳 Secure payment powered by Stripe</p>
          <p className="small-text">Cancel anytime. No hidden fees.</p>
        </div>
      </div>
    </div>
  );
};

export default Payment;